/**
 * When older extractions put everything in explanation, pull out the file-summary block.
 */
export function extractFileSummarySection(text) {
  const raw = String(text || '').trim();
  if (!raw) {
    return { summary: '', remainder: '' };
  }
  const markerMatch = raw.match(/\*\*What these files contain:\*\*\s*/i);
  if (!markerMatch) {
    return { summary: '', remainder: raw };
  }
  const start = raw.indexOf(markerMatch[0]) + markerMatch[0].length;
  const flagsIdx = raw.search(/\bFlags:\s*/i);
  const end = flagsIdx >= 0 ? flagsIdx : raw.length;
  const summary = raw.slice(start, end).trim();
  const before = raw.slice(0, raw.indexOf(markerMatch[0])).trim();
  const after = flagsIdx >= 0 ? raw.slice(flagsIdx).trim() : '';
  const remainder = [before, after].filter(Boolean).join('\n\n').trim();
  return { summary, remainder };
}

/**
 * Split snapshot explanation from file-summary content (avoid duplicate blobs in UI).
 */
export function explanationWithoutEmbeddedFileSummary(explanation, documentSummary) {
  let text = String(explanation || '').trim();
  const summary = String(documentSummary || '').trim();
  if (!text) {
    return '';
  }
  if (!summary) {
    return text;
  }

  const marker = '**What these files contain:**';
  if (text.startsWith(marker)) {
    text = text.slice(marker.length).trim();
  }
  if (text.startsWith(summary)) {
    text = text.slice(summary.length).trim();
  } else if (summary.length > 40 && text.includes(summary.slice(0, Math.min(120, summary.length)))) {
    const idx = text.indexOf(summary.slice(0, Math.min(120, summary.length)));
    text = (text.slice(0, idx) + text.slice(idx + summary.length)).trim();
  }
  return text.replace(/^\n+/, '').trim();
}

/**
 * Parse model documentSummary into per-file rows for the results panel.
 */
export function parseDocumentSummaryEntries(documentSummary) {
  const raw = String(documentSummary || '').trim();
  if (!raw) {
    return [];
  }

  const lines = raw.split(/\n+/).map((line) => line.trim()).filter(Boolean);
  const entries = [];

  for (const line of lines) {
    const bullet = line.replace(/^[-*•]\s+/, '').trim();
    const boldMatch = bullet.match(/^\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/);
    if (boldMatch) {
      entries.push({ title: boldMatch[1].trim(), body: boldMatch[2].trim() });
      continue;
    }
    const backtickMatch = bullet.match(/^`([^`]+)`\s*[—–:-]\s*(.+)$/);
    if (backtickMatch) {
      entries.push({ title: backtickMatch[1].trim(), body: backtickMatch[2].trim() });
      continue;
    }
    const colonSplit = bullet.match(/^(.+?\.(?:xlsx|xls|csv|pdf|pptx?|docx?))\s*[—–:-]\s*(.+)$/i);
    if (colonSplit) {
      entries.push({ title: colonSplit[1].trim(), body: colonSplit[2].trim() });
      continue;
    }
    if (entries.length === 0) {
      entries.push({ title: '', body: bullet });
    } else {
      entries[entries.length - 1].body = `${entries[entries.length - 1].body} ${bullet}`.trim();
    }
  }

  if (entries.length === 0 && raw) {
    return [{ title: '', body: raw }];
  }
  if (entries.length === 1 && entries[0].title === '' && /\*\*[^*]+\*\*/.test(raw)) {
    const parts = raw.split(/(?=\*\*[^*]+\*\*)/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      return parts.map((part) => {
        const m = part.match(/^\*\*(.+?)\*\*\s*[—–:-]?\s*(.*)$/s);
        if (m) {
          return { title: m[1].trim(), body: m[2].trim() || '—' };
        }
        return { title: '', body: part };
      });
    }
  }
  return entries;
}

/**
 * Split "Flags:" block from synthesis explanation for separate rendering.
 */
export function splitExplanationNotesAndFlags(explanation) {
  const text = String(explanation || '').trim();
  if (!text) {
    return { notes: '', flags: '' };
  }
  const flagsIdx = text.search(/\bFlags:\s*/i);
  if (flagsIdx < 0) {
    return { notes: text, flags: '' };
  }
  const notes = text.slice(0, flagsIdx).trim();
  const flags = text.slice(flagsIdx).replace(/^Flags:\s*/i, '').trim();
  return { notes, flags };
}
